import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import LookCard from '../../components/store/LookCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { publicAPI, leadAPI, seedDatabase } from '../../lib/api';
import { trackLead } from '../../lib/tracking';
import { toast } from 'sonner';

export default function HomePage() {
  const { t } = useTranslation();
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Try to seed database first (will skip if already seeded)
      await seedDatabase().catch(() => {});
      
      const response = await publicAPI.getLooks({ limit: 3 });
      setLooks(response.data.items);
    } catch (error) {
      console.error('Error loading looks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      await leadAPI.create({
        email,
        source: 'NEWSLETTER',
        consent_marketing: true,
      });
      trackLead('newsletter');
      toast.success('Merci pour votre inscription !');
      setEmail('');
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <StoreLayout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center store-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1652281846260-14c1bdd5e9a0?w=1920&q=85"
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none">
              {t('home.hero.title')}
              <br />
              <span className="text-muted-foreground">{t('home.hero.subtitle')}</span>
            </h1>
            
            <p className="mt-8 text-lg text-muted-foreground max-w-md leading-relaxed">
              Des looks complets soigneusement sélectionnés pour vous. 
              Gagnez du temps, affirmez votre style.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button size="lg" className="group" asChild data-testid="hero-cta">
                <Link to="/looks">
                  {t('home.hero.cta')}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/quiz">
                  <Sparkles className="mr-2 h-4 w-4" />
                  {t('home.quiz.cta')}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Looks */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-end justify-between mb-12"
          >
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                {t('home.featured')}
              </h2>
              <p className="mt-2 text-muted-foreground">
                Nos sélections les plus populaires
              </p>
            </div>
            <Button variant="link" className="hidden sm:flex" asChild>
              <Link to="/looks">
                Voir tout
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-muted rounded" />
                  <div className="mt-4 h-4 bg-muted rounded w-1/3" />
                  <div className="mt-2 h-6 bg-muted rounded w-2/3" />
                  <div className="mt-2 h-4 bg-muted rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="featured-looks">
              {looks.map((look, index) => (
                <LookCard key={look.id} look={look} index={index} />
              ))}
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" asChild>
              <Link to="/looks">
                Voir tous les looks
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Quiz CTA */}
      <section className="py-24 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                {t('home.quiz.title')}
              </h2>
              <p className="mt-4 text-background/70 text-lg leading-relaxed">
                {t('home.quiz.description')}
              </p>
              <Button 
                size="lg" 
                variant="secondary" 
                className="mt-8 group"
                asChild
                data-testid="quiz-cta"
              >
                <Link to="/quiz">
                  <Sparkles className="mr-2 h-4 w-4" />
                  {t('home.quiz.cta')}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1647070045776-eba8e5178cee?w=800&q=85"
                  alt="Quiz style"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              {t('home.newsletter.title')}
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              {t('home.newsletter.description')}
            </p>

            <form onSubmit={handleSubscribe} className="mt-8 flex gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder={t('home.newsletter.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                required
                data-testid="newsletter-email"
              />
              <Button 
                type="submit" 
                disabled={subscribing}
                data-testid="newsletter-submit"
              >
                {subscribing ? '...' : t('home.newsletter.cta')}
              </Button>
            </form>

            <p className="mt-4 text-xs text-muted-foreground">
              En vous inscrivant, vous acceptez de recevoir nos communications marketing.
            </p>
          </motion.div>
        </div>
      </section>
    </StoreLayout>
  );
}
